import { FC, ReactNode, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import Card from '../components/layout/Card';

import {
  HoleInOneClaimStatus,
  HoleInOneClaimStatuses,
  UpdateHoleInOneClaimStatusData,
} from '@/api/holeInOneClaim/holeInOneClaim.types.ts';
import Button from '@/components/controls/Button.tsx';
import CustomConfirmationMessageModal from '@/components/controls/CustomConfirmationMessageModal.tsx';
import HoleInOneClaimDetailsActionButtons from '@/components/layout/holeInOneClaimDetailsPage/HoleInOneClaimDetailsActionButtons.tsx';
import HoleInOneClaimDetailsGrid from '@/components/layout/holeInOneClaimDetailsPage/HoleInOneClaimDetailsGrid.tsx';
import HoleInOneClaimDetailsList from '@/components/layout/holeInOneClaimDetailsPage/HoleInOneClaimDetailsList.tsx';
import HoleInOneClaimDetailsLocation from '@/components/layout/holeInOneClaimDetailsPage/HoleInOneClaimDetailsLocation.tsx';
import HoleInOneClaimDetailsMedia from '@/components/layout/holeInOneClaimDetailsPage/HoleInOneClaimDetailsMedia.tsx';
import { useHoleInOneClaims } from '@/hooks/useHoleInOneClaims.ts';
import { useHoleInOneClaimDetailsStore } from '@/stores/useHoleInOneClaimDetailsStore.ts';

const ClaimDetailsPage: FC = () => {
  const holeInOneClaimDetails = useHoleInOneClaimDetailsStore(
    (state) => state.holeInOneClaimDetails
  );

  const [claimStatus, setClaimStatus] = useState<HoleInOneClaimStatus | null>(
    holeInOneClaimDetails ? holeInOneClaimDetails.status : null
  );

  const { updateClaimStatus } = useHoleInOneClaims();

  const [showConfirmStatusModal, setShowConfirmStatusModal] = useState(false);
  const [confirmStatusText, setConfirmStatusText] = useState('');

  const navigate = useNavigate();

  const handleApprove = (): void => {
    setClaimStatus(HoleInOneClaimStatuses.APPROVED);

    setShowConfirmStatusModal(true);
    setConfirmStatusText(
      'Please confirm that you want to approve this reviewed claim. Note that once approved, no further changes can be made.'
    );
  };

  const handleReject = (): void => {
    setClaimStatus(HoleInOneClaimStatuses.REJECTED);

    setShowConfirmStatusModal(true);
    setConfirmStatusText(
      'Please confirm that you want to reject this reviewed claim. Note that once rejected, no further changes can be made.'
    );
  };

  const handleCancel = (): void => {
    setClaimStatus(holeInOneClaimDetails.status);

    setShowConfirmStatusModal(false);
    setConfirmStatusText('');
  };

  const handleConfirm = async (): Promise<void> => {
    const updateClaimStatusData: UpdateHoleInOneClaimStatusData =
      claimStatus === HoleInOneClaimStatuses.APPROVED
        ? {
            status: HoleInOneClaimStatuses.APPROVED,
          }
        : {
            status: HoleInOneClaimStatuses.REJECTED,
          };

    await updateClaimStatus(holeInOneClaimDetails.id, updateClaimStatusData);

    setShowConfirmStatusModal(false);
    setConfirmStatusText('');

    const { reset } = useHoleInOneClaimDetailsStore.getState();
    reset();

    navigate('/claims-list');
  };

  const handleClose = (): void => {
    const { reset } = useHoleInOneClaimDetailsStore.getState();
    reset();

    navigate('/claims-list');
  };

  if (!holeInOneClaimDetails) {
    return null;
  }

  const cardCloseButton: ReactNode = (
    <Button
      buttonStyle="justify-center items-center px-5 py-2 text-base font-semibold border border-primary-100 text-primary-100 whitespace-nowrap bg-inherit hover:bg-gray-100 rounded-lg btn-custom-width"
      text="Close"
      buttonType={{ type: 'button' }}
      hasImage={false}
      onClick={handleClose}
    />
  );

  return (
    <>
      <Card cardTitle="Claim Details" cardButton={cardCloseButton}>
        <HoleInOneClaimDetailsGrid claimDetails={holeInOneClaimDetails} />
        {holeInOneClaimDetails.status === HoleInOneClaimStatuses.SUBMITTED && (
          <HoleInOneClaimDetailsActionButtons
            onApprove={handleApprove}
            onReject={handleReject}
          />
        )}
      </Card>
      <Card cardTitle="Location">
        <HoleInOneClaimDetailsLocation
          latitude={parseFloat(holeInOneClaimDetails.latitude)}
          longitude={parseFloat(holeInOneClaimDetails.longitude)}
          holeNumber={holeInOneClaimDetails.holeNumber}
        />
      </Card>
      <Card cardTitle="Witnesses">
        <HoleInOneClaimDetailsList
          claimWitnesses={holeInOneClaimDetails.claimWitnesses}
          noWitnessesText={`No witnesses for ${holeInOneClaimDetails.status} Hole-in-One claim.`}
        />
      </Card>
      <Card cardTitle="Uploaded Media">
        <HoleInOneClaimDetailsMedia
          mediaEvidence={holeInOneClaimDetails.claimEvidence}
        />
      </Card>
      <CustomConfirmationMessageModal
        isOpen={showConfirmStatusModal}
        title="Confirm action: change status"
        message={confirmStatusText}
        onCancel={handleCancel}
        cancelButtonText="Cancel"
        onConfirm={handleConfirm}
        confirmButtonText="Confirm"
      />
    </>
  );
};

export default ClaimDetailsPage;
